import type { FC } from 'react';
import { Typography } from '@mui/material';

import { usePaymentMethod } from '@mopla/business-logic';
import {
	AmazonPayIcon,
	KlarnaIcon,
	PayByBankIcon,
	PaymentIcon,
	PaypalIcon,
	RevolutPayIcon,
	SepaIconV2,
	VisaIcon,
} from '@mopla/ui';

import { CardExpiryDateStyled, Root } from './PaymentDetails.styles';

const hiddenCardNumber = '****';

interface IPaymentDetailsProps {
	/**
	 * Optional label prepended before the masked number for card methods
	 * (e.g. "Kreditkarte"). When omitted only the masked number is shown.
	 */
	cardLabel?: string;
}

export const PaymentDetails: FC<IPaymentDetailsProps> = ({ cardLabel }) => {
	const { defaultPaymentMethod } = usePaymentMethod();

	if (!defaultPaymentMethod) return <Root></Root>;

	const getIcon = () => {
		switch (defaultPaymentMethod.type) {
			case 'card': {
				if (defaultPaymentMethod.card?.brand === 'visa') return <VisaIcon />;
				return <PaymentIcon />; // TODO: if necessary -> replace with MasterCard or any other brand's icons
			}
			case 'paypal':
				return <PaypalIcon />;
			case 'revolut_pay':
				return <RevolutPayIcon />;
			case 'amazon_pay':
				return <AmazonPayIcon />;
			case 'klarna':
				return <KlarnaIcon />;
			case 'sepa_debit':
				return <SepaIconV2 />;
			case 'pay_by_bank':
				return <PayByBankIcon />;
			default:
				return <PaymentIcon />;
		}
	};

	const getPaymentText = () => {
		switch (defaultPaymentMethod.type) {
			case 'paypal':
				return defaultPaymentMethod.paypal?.payer_email;
			case 'klarna':
				return 'Klarna';
			case 'revolut_pay':
				return 'Revolut Pay';
			case 'amazon_pay':
				return 'Amazon Pay';
			case 'pay_by_bank':
				return 'Pay by Bank';
			case 'card':
				return cardLabel
					? `${cardLabel} ${hiddenCardNumber}`
					: hiddenCardNumber;
			default:
				return hiddenCardNumber;
		}
	};

	const getLast4 = () => {
		switch (defaultPaymentMethod.type) {
			case 'card': {
				return defaultPaymentMethod.card?.last4;
			}
			case 'sepa_debit': {
				return defaultPaymentMethod.sepa_debit?.last4;
			}
			default:
				return '';
		}
	};

	return (
		<Root>
			{getIcon()}
			<Typography
				variant="body1"
				noWrap
				data-testid="payment-details-email-and-last4-digits"
			>
				{getPaymentText()} {getLast4()}
			</Typography>
			{defaultPaymentMethod.card && (
				<CardExpiryDateStyled
					data-testid="payment-default-payment-method-card"
					card={defaultPaymentMethod.card}
				/>
			)}
		</Root>
	);
};
